import React, { useState } from 'react';
import {
  App, Button, Input, Modal, Space, Tooltip, Typography,
} from 'antd';
import { ButtonProps } from 'antd/es/button/button';
import { ExclamationCircleFilled, KeyOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../store/auth';
import { UserRole, UserStatusEnum } from '../../../../enums/user';
import { useMessageError } from '../../../../hooks/common';
import { useSimpleModal } from '../../../Common/Modal/Simple';
import { UserTableRow } from '../../../../hooks/api/users';
import { useAdminLoginAsUser, useAdminLoginAsUserVerify } from '../../../../hooks/api/superAdminAccess';

const { Text, Link } = Typography;

interface SuperAdminAccessButtonProps {
  record: UserTableRow;
  buttonProps?: ButtonProps;
}

export default function SuperAdminAccessButton({
  record, buttonProps,
}: SuperAdminAccessButtonProps): React.ReactNode {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { open, contextHolder } = useSimpleModal();

  const loginAsUser = useAdminLoginAsUser();
  const loginAsUserVerify = useAdminLoginAsUserVerify();

  const [otpModalVisible, setOtpModalVisible] = useState(false);
  const [code, setCode] = useState('');
  const [portalUrl, setPortalUrl] = useState<string | null>(null);

  useMessageError([loginAsUser, loginAsUserVerify]);

  if (!user || ![UserRole.ADMIN, UserRole.ROOT].includes(user.role as UserRole)) {
    return null;
  }

  const isUserActive = record.status === UserStatusEnum.ACTIVE;

  const requestAccess = () => {
    loginAsUser.fetch({ userId: record.id }).then((res) => {
      if (res?.requiresOtpVerification) {
        setCode('');
        setPortalUrl(null);
        setOtpModalVisible(true);
      }
    });
  };

  const handleClick = () => {
    if (!user.isTwoFAActive) {
      open({
        icon: <ExclamationCircleFilled />,
        title: 'Two-Factor Authentication required',
        content: (
          <span>
            You need to set up Two-Factor Authentication for your own account before requesting Super Admin access.
          </span>
        ),
        okText: 'Set up 2FA',
        cancelText: 'Cancel',
        onOk: () => navigate(`/admins/${user.id}/edit`),
      });

      return;
    }

    requestAccess();
  };

  const handleVerify = () => {
    if (code.length !== 6) {
      return;
    }

    loginAsUserVerify.fetch({ userId: record.id, code }).then((res) => {
      if (res?.ssoCode) {
        const portalBaseUrl = process.env.REACT_APP_USER_PORTAL_URL || '';
        const url = new URL(`${portalBaseUrl}sso`);

        url.searchParams.set('ssoCode', res.ssoCode);

        message.success('Super Admin access granted');
        setPortalUrl(url.toString());
      }
    });
  };

  const closeModal = () => {
    setOtpModalVisible(false);
    setCode('');
    setPortalUrl(null);
  };

  return (
    <>
      <Tooltip title={isUserActive ? undefined : 'Super Admin access can only be granted to active users.'}>
        <span>
          <Button
            icon={<KeyOutlined />}
            loading={loginAsUser.loading}
            title="Get Super Admin Access"
            onClick={handleClick}
            {...buttonProps}
            disabled={!isUserActive || buttonProps?.disabled}
            style={{
              ...buttonProps?.style,
              ...(isUserActive ? {} : { pointerEvents: 'none' }),
            }}
          >
            Get Super Admin Access
          </Button>
        </span>
      </Tooltip>

      <Modal
        title={portalUrl ? 'Super Admin Access Granted' : 'Confirm Super Admin Access'}
        open={otpModalVisible}
        centered
        maskClosable={false}
        onCancel={closeModal}
        footer={portalUrl ? [
          <Button key="close" onClick={closeModal}>
            Close
          </Button>,
        ] : [
          <Button key="cancel" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            disabled={code.length !== 6}
            loading={loginAsUserVerify.loading}
            onClick={handleVerify}
          >
            Verify & Continue
          </Button>,
        ]}
      >
        {portalUrl ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Text type="secondary">
              Super Admin access to
              {' '}
              <b>{record.email}</b>
              &apos;s account has been granted. Click the link below to open the User Portal.
            </Text>
            <Link href={portalUrl} target="_blank" rel="noopener noreferrer">
              <LinkOutlined />
              {' '}
              {portalUrl}
            </Link>
          </Space>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Text type="secondary">
              Enter the 6-digit verification code from your authenticator app to get Super Admin access to
              {' '}
              <b>{record.email}</b>
              &apos;s account.
            </Text>
            <Input
              maxLength={6}
              placeholder="000000"
              size="large"
              value={code}
              autoFocus
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onPressEnter={handleVerify}
              style={{
                textAlign: 'center', letterSpacing: '4px', fontSize: '18px',
              }}
            />
          </Space>
        )}
      </Modal>
      {contextHolder}
    </>
  );
}
